import LabeledSelect from 'components/LabeledSelect';
import { useEffect, useState } from 'react';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { buildDevicesBySystemAndTypeMap } from '../utils/globalActionsUtils';
import CreateRadialAlert, { VerifyRadialAlert } from './DeviceActions/CreateRadialAlert';
import ToggleFlashingLights from './DeviceActions/ToggleFlashingLights';

const normalizeTypeKey = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

const DeviceAction = ({ isGroup = false, setData, data, setError }) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const devicesBySystemAndTypeMap = buildDevicesBySystemAndTypeMap(connectedDevices);

  const [systemType, setSystemType] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [deviceSubType, setDeviceSubType] = useState('');
  const [deviceGroupsList, setDeviceGroupsList] = useState([]);
  const [group, setGroup] = useState();

  useEffect(() => {
    if (deviceGroups) {
      setDeviceGroupsList(deviceGroups);
    }
  }, [deviceGroups]);

  useEffect(() => {
    if (
      (data?.node_id === undefined && data?.group_id === undefined) ||
      data?.deviceType === undefined ||
      data?.selectedDevice === undefined ||
      data?.selectedFunction === undefined
    ) {
      setData({
        group_id: '',
        node_id: '',
        deviceType: '',
        selectedDevice: '',
        selectedFunction: '',
        error: true,
      });
    }

    if (data?.group_id) {
      const foundGroup = deviceGroupsList.find((g) => g.id === data.group_id);
      setGroup(foundGroup ?? null);
    }

    if (data?.deviceType) {
      setDeviceType(data.selectedDevice?.device_type || '');
      setSystemType(data.selectedDevice?.system_type || '');
      setDeviceSubType('');
    }
  }, [deviceGroupsList]);

  const updateData = (item) => {
    let newObj = { ...data, ...item };
    // Trim keys to what the active function needs + universal ones
    newObj = Object.keys(newObj).reduce((acc, key) => {
      if (
        deviceFunctions[newObj.selectedFunction]?.keys.includes(key) ||
        universalKeys.includes(key)
      )
        acc[key] = newObj[key];
      return acc;
    }, {});

    setData({
      ...newObj,
      error: verifyData(newObj),
    });
  };

  const verifyData = (obj) => {
    let error =
      (obj.node_id === '' && obj.group_id === '') ||
      obj.deviceType === '' ||
      obj.selectedFunction === '';

    // Verify function-specific payload
    error = deviceFunctions[obj.selectedFunction]?.verify
      ? !deviceFunctions[obj.selectedFunction].verify(obj)
      : true;

    if (!error) setError(null);
    return error;
  };

  const functionList = {
    'device group': ['Create Radial Alert', 'Toggle Flashing Lights', 'Toggle Siren/Giant Voice'],
    'flood stage': ['Create Radial Alert'],
    'rescalert devices manual road closure': ['Toggle Manual Road Closure'],
    'rescalert devices technology bridge_flashing lights': ['Toggle Flashing Lights'],
    'rescalert devices technology bridge_siren/giant voice': ['Toggle Siren/Giant Voice'],
    'technology bridge_flashing lights': ['Toggle Flashing Lights'],
    'technology bridge_siren/giant voice': ['Toggle Siren/Giant Voice'],
    'usgs monitoring station': ['Create Radial Alert'],
  };

  const deviceFunctions = {
    'Create Radial Alert': {
      component: (
        <CreateRadialAlert dataObj={data} deviceList={connectedDevices} onUpdate={updateData} />
      ),
      verify: (obj) => {
        const verified = VerifyRadialAlert(obj);
        if (!verified) setError({ message: 'Action incomplete, check for missing fields.' });
        return verified;
      },
      keys: ['headline', 'description', 'type', 'radius', 'severity'],
    },
    'Toggle Manual Road Closure': {
      component: (
        <ToggleFlashingLights
          dataObj={data}
          deviceList={connectedDevices}
          onUpdate={updateData}
          group={group}
        />
      ),
      verify: (obj) => {
        if (obj.status === null || obj.status === undefined || (!obj.node_id && !obj.group_id)) {
          setError({ message: 'Action incomplete, check for missing fields.' });
          return false;
        }
        return true;
      },
      keys: ['status', 'comment'],
    },
    'Toggle Flashing Lights': {
      component: (
        <ToggleFlashingLights
          dataObj={data}
          deviceList={connectedDevices}
          onUpdate={updateData}
          group={group}
        />
      ),
      verify: (obj) => {
        if (obj.status === null || obj.status === undefined || (!obj.node_id && !obj.group_id)) {
          setError({ message: 'Action incomplete, check for missing fields.' });
          return false;
        }
        return true;
      },
      keys: ['status', 'comment'],
    },
    'Toggle Siren/Giant Voice': {
      component: (
        <ToggleFlashingLights
          dataObj={data}
          deviceList={connectedDevices}
          onUpdate={updateData}
          group={group}
        />
      ),
      verify: (obj) => {
        if (obj.status === null || obj.status === undefined || (!obj.node_id && !obj.group_id)) {
          setError({ message: 'Action incomplete, check for missing fields.' });
          return false;
        }
        return true;
      },
      keys: ['status', 'comment', 'interval'],
    },
  };

  const universalKeys = ['node_id', 'group_id', 'deviceType', 'selectedDevice', 'selectedFunction'];

  const Toolbar = ({ children }) => (
    <div className="d-flex flex-column flex-md-row p-3 rounded-2 m-2 gap-3 bg-primary align-items-stretch align-items-md-center">
      {children}
    </div>
  );

  if (!isGroup) {
    const hasSystem = !!systemType && !!deviceTypes[systemType];

    const normalizedTypeKey = normalizeTypeKey(data?.deviceType);

    return (
      <div className="d-flex flex-column w-100">
        <Toolbar>
          <LabeledSelect
            label="System:"
            value={systemType || ''}
            onChange={(e) => {
              setSystemType(e.target.value);
              setDeviceType('');
              setDeviceSubType('');

              updateData({
                node_id: '',
                deviceType: '',
                selectedDevice: '',
                selectedFunction: '',
                error: true,
              });
            }}
            className="flex-fill max-w-sm"
          >
            <option value="">-Select-</option>
            {Object.keys(deviceTypes)
              .filter((key) => key.toLowerCase() !== 'exacq vision')
              .map((type) => {
                if (!devicesBySystemAndTypeMap.has(type)) return null;
                return (
                  <option key={type} value={type}>
                    {type}
                  </option>
                );
              })}
          </LabeledSelect>

          {hasSystem && (
            <LabeledSelect
              label="Device Type:"
              value={deviceType || ''}
              onChange={(e) => {
                const nextType = e.target.value;
                setDeviceType(nextType);
                setDeviceSubType('');

                const sys = deviceTypes[systemType];
                const node = sys?.[nextType];

                if (
                  node &&
                  (Object.keys(node).length === 0 || Object.keys(node).includes('validDevices'))
                ) {
                  updateData({
                    deviceType: `${systemType} ${nextType}`,
                    node_id: '',
                    selectedDevice: '',
                    selectedFunction: '',
                    error: true,
                  });
                } else {
                  updateData({
                    node_id: '',
                    deviceType: '',
                    selectedDevice: '',
                    selectedFunction: '',
                    error: true,
                  });
                }
              }}
              className="flex-fill max-w-sm"
            >
              <option value="">-Select-</option>
              {Object.keys(deviceTypes[systemType])
                .filter((key) => key.toLowerCase() !== 'overwatch vms')
                .map((type) => {
                  const devices = devicesBySystemAndTypeMap.get(systemType).get(type) || [];
                  if (devices.length === 0) return null;
                  return (
                    <option key={type} value={type}>
                      {type.replace('_', ' ')}
                    </option>
                  );
                })}
            </LabeledSelect>
          )}

          {data?.deviceType && functionList[normalizeTypeKey(data.deviceType)] && (
            <LabeledSelect
              label="Device Action:"
              value={data.selectedFunction || ''}
              onChange={(e) => updateData({ selectedFunction: e.target.value })}
              className="flex-fill max-w-sm"
            >
              <option value="">-Select-</option>
              {functionList[normalizedTypeKey].map((func) => (
                <option key={func} value={func}>
                  {func}
                </option>
              ))}
            </LabeledSelect>
          )}
        </Toolbar>

        {data.selectedFunction && deviceFunctions[data.selectedFunction]?.component}
      </div>
    );
  }

  //Group
  return (
    <div className="d-flex flex-column w-100">
      <Toolbar>
        <LabeledSelect
          label="Device Group:"
          value={group?.name || ''}
          onChange={(e) => {
            setSystemType('RescAlert Devices');
            const foundGroup = deviceGroupsList.find((g) => g.name === e.target.value);
            if (!foundGroup) return;

            setGroup(foundGroup);

            updateData({
              deviceType: foundGroup.type,
              group_id: foundGroup.id,
              selectedFunction: '',
              status: 0,
              error: true,
            });
          }}
          className="flex-fill max-w-sm"
        >
          <option value="">-Select-</option>
          {deviceGroupsList.map((g) => (
            <option key={g.id} value={g.name}>
              {g.name}
            </option>
          ))}
        </LabeledSelect>

        {data?.deviceType && functionList[normalizeTypeKey(data?.deviceType)] && (
          <LabeledSelect
            label="Group Action:"
            value={data.selectedFunction || ''}
            onChange={(e) => updateData({ selectedFunction: e.target.value })}
            className="flex-fill max-w-sm"
          >
            <option value="">-Select-</option>
            {functionList[normalizeTypeKey(data?.deviceType)].map((func) => (
              <option key={func} value={func}>
                {func}
              </option>
            ))}
          </LabeledSelect>
        )}
      </Toolbar>

      {data.selectedFunction && deviceFunctions[data.selectedFunction]?.component}
    </div>
  );
};

export default DeviceAction;
